import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import Avatar from "@/components/common/Avatar";
import Back from "@/components/specific/Back";
import WebIcon from "@/components/common/Web_Icons";
import Button from "@/components/common/Button";
import Delete from "@/components/common/alerts/Delete";
import Success from "@/components/common/alerts/Success";
import { useLoader } from "@/hooks/LoaderProvider";
import Breadcrumb from "@/components/specific/Breadcrumb";
import { useParams } from "react-router-dom";
import {
  fetchSingleTicketAsync,
  fetchTicketRepliesAsync,
  selectSingleTicket,
  selectSingleTicketLoading,
  selectTicketReplies,
  updateTicketStatusAsync,
  selectStatusUpdateLoading,
  selectStatusUpdateSuccess,
  clearStatusUpdateSuccess,
} from "@/store/redux/slices/support/support_slice";
import { selectUserProfile } from "@/store/redux/slices/user/user_slice";
import {
  sortSupportMessagesChronologically,
} from "@/utils/supportUtils";
import ReplyTicketModal from "@/pages/support/Reply_Ticket_Modal";

const SingleSupport = () => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const { showLoader, hideLoader } = useLoader();
  const { id } = useParams();
  const dispatch = useDispatch();

  const ticket = useSelector(selectSingleTicket);
  const ticketReplies = useSelector(selectTicketReplies);
  const isLoading = useSelector(selectSingleTicketLoading);
  const statusUpdateLoading = useSelector(selectStatusUpdateLoading);
  const statusUpdateSuccess = useSelector(selectStatusUpdateSuccess);
  const userProfile = useSelector(selectUserProfile);
  const sortedReplies = sortSupportMessagesChronologically(ticketReplies);

  // Ticket via by-any; replies via GET /v1/supportTicket?ticket={id}.
  const fetchTicketWithReplies = useCallback(async () => {
    showLoader();
    try {
      await Promise.all([
        dispatch(fetchSingleTicketAsync({ key: "_id", value: id })).unwrap(),
        dispatch(fetchTicketRepliesAsync({ ticketId: id })).unwrap(),
      ]);
    } catch (error) {
      console.error("Error fetching ticket details:", error);
    } finally {
      hideLoader();
    }
  }, [dispatch, hideLoader, id, showLoader]);

  useEffect(() => {
    fetchTicketWithReplies();
  }, [fetchTicketWithReplies]);

  const handleCloseClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteClose = () => {
    setShowDeleteModal(false);
  };

  const handleDeleteProceed = async () => {
    try {
      showLoader();
      setShowDeleteModal(false);
      await dispatch(
        updateTicketStatusAsync({
          id: ticket?._id || id,
          status: "Closed",
        })
      ).unwrap();
    } catch (error) {
      console.error("Error closing ticket:", error);
    } finally {
      hideLoader();
    }
  };

  const handleSuccessClose = async () => {
    await dispatch(clearStatusUpdateSuccess());
    await fetchTicketWithReplies();
  };

  const handleReplyClick = () => {
    setShowReplyModal(true);
  };

  const handleReplyClose = () => {
    setShowReplyModal(false);
  };

  const handleReplySent = () => {
    fetchTicketWithReplies();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const breadcrumbItems = [
    { title: "Support", link: "/dashboard/support" },
    { title: `Ticket #${ticket?.id || id}`, link: `/dashboard/support/${id}` },
  ];

  if (isLoading && !ticket) {
    return null;
  }

  return (
    <div className="p-4">
      <div>
        <Breadcrumb items={breadcrumbItems} />
      </div>
      <div className="mb-6">
        <Back text="Back" />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="w-full col-span-2 p-2 rounded-md lg:border">
          <div className="mb-6 text-left">
            <h1 className="mb-4 heading-large">{ticket?.subject || ""}</h1>

            <div className="flex items-center gap-4">
              <WebIcon icon="Trash2" className="w-6 h-6 cursor-pointer" />
              <WebIcon icon="Export" className="w-6 h-6 cursor-pointer" />
              <div
                className="flex items-center gap-2 cursor-pointer"
                onClick={handleReplyClick}>
                <WebIcon icon="ReplyFilled" className="w-6 h-6" />
                <span className="text-regular">Reply</span>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-6">
            {/* Original ticket message */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Avatar
                    size={40}
                    src={ticket?.userID?.profileImage || ""}
                    alt={ticket?.userID?.fullname || "User"}
                  />
                  <div className="flex flex-col text-left">
                    <h3 className="text-bold">{ticket?.userID?.fullname || ""}</h3>
                    <span className="text-lighterGrey text-small-xs">
                      {ticket?.priority ? `${ticket.priority} Priority` : ""}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <WebIcon icon="CalenderFilled" className="w-5 h-5" />
                  <span className="text-[11px]">
                    {formatDate(ticket?.createdAt)}
                  </span>
                </div>
              </div>

              <div className="p-6 font-normal rounded-lg bg-secondary text-darkGrey">
                <div className="space-y-6 font-normal text-left text-medium">
                  <p>{ticket?.description || ""}</p>
                </div>
              </div>
            </div>

            {/* Admin replies */}
            {sortedReplies.map((reply) => (
              <div key={reply._id || reply.id}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Avatar
                      size={40}
                      src={
                        reply?.userID?.profileImage ||
                        userProfile?.profileImage ||
                        ""
                      }
                      alt={reply?.userID?.fullname || "Support"}
                    />
                    <div className="flex flex-col text-left">
                      <h3 className="text-bold text-lighterGrey">
                        {reply?.userID?.fullname || userProfile?.fullname || ""}
                      </h3>
                      <span className="text-gray-400 text-small-xs">
                        Customer Support
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <WebIcon
                      icon="CalenderFilled"
                      className="w-4 h-4 text-gray-400"
                    />
                    <span className="text-[11px]">
                      {formatDate(reply?.createdAt)}
                    </span>
                  </div>
                </div>

                <div className="p-6 mt-4 font-normal text-left rounded-lg bg-secondary text-lighterGrey">
                  <div className="text-regular">{reply?.description || ""}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="w-full mt-10 text-left lg:mt-0">
          <div className="rounded-md lg:border lg:p-2">
            <h2 className="heading-medium text-lighterGrey">Ticket Info</h2>
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2 mb-4" />
              <div className="flex items-center gap-2">
                <WebIcon icon="Support" className="w-5 h-5" />
                <span className="text-regular">
                  Status: {ticket?.status || ""}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <WebIcon icon="ExpiredTicket" className="w-5 h-5" />
                <span className="text-regular">{ticket?.id || ""}</span>
              </div>
              <div className="flex items-center gap-2">
                <WebIcon icon="CalenderFilled" className="w-5 h-5" />
                <span className="text-regular">
                  {formatDate(ticket?.createdAt)}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-5">
            <Button
              text="CLOSE"
              icon={<WebIcon icon="DocumentCode" className="w-5 h-5" />}
              iconPosition="left"
              customBgColor="white"
              customTextColor="primary"
              className="w-full border h-[36px] border-primary"
              rounded="lg"
              fontWeight="normal"
              onClick={handleCloseClick}
              disabled={ticket?.status === "Closed" || statusUpdateLoading}
            />
          </div>
        </div>
      </div>

      <Delete
        isVisible={showDeleteModal}
        onClose={handleDeleteClose}
        onProceed={handleDeleteProceed}
        header="Close Ticket?"
        paragraph="By closing this ticket, it means the issue at hand has been resolved and the customer will be alerted about this closure"
        proceedButtonText="Proceed"
        cancelButtonText="Cancel"
      />

      <Success
        isVisible={statusUpdateSuccess}
        onClose={handleSuccessClose}
        header="Ticket Closed"
        paragraph='The ticket owner will be alerted about the closure of the ticket as the issuer has been marked as <strong>"solved"</strong>.'
        buttonText="Close"
      />

      <ReplyTicketModal
        isVisible={showReplyModal}
        onClose={handleReplyClose}
        recipientName={ticket?.userID?.fullname || ""}
        supportTicketID={ticket?._id || id}
        onReplySent={handleReplySent}
      />
    </div>
  );
};

export default SingleSupport;
